import { ThemeEvents } from '@theme/events';
import { debounce } from '@theme/utilities';

/**
 * Positions the Shopify account sheet like the legacy `anchored-popover-component` +
 * `.account-popover__panel` rules: below the trigger, right edge aligned with the trigger.
 *
 * Only runs at desktop widths (matches theme drawer breakpoint). Mobile keeps Shopify defaults.
 *
 * Do not clear inline position on `close` — that runs while the exit animation is still
 * visible and makes the sheet jump to the default top-right before fading.
 */

/** @type {string} Viewport min-width for anchored desktop sheet (same as legacy account-drawer) */
const DESKTOP_ACCOUNT_SHEET_MQ = '(min-width: 750px)';

function isDesktopAccountSheetLayout() {
  return window.matchMedia(DESKTOP_ACCOUNT_SHEET_MQ).matches;
}

/**
 * @param {Document | ShadowRoot} root
 * @param {HTMLElement[]} out
 */
function collectSheetCandidates(root, out) {
  for (const sel of ['dialog', '[popover]', '[role="dialog"]']) {
    for (const node of root.querySelectorAll(sel)) {
      if (node instanceof HTMLElement) {
        out.push(node);
      }
    }
  }

  for (const el of root.querySelectorAll('*')) {
    if (el.shadowRoot) {
      collectSheetCandidates(el.shadowRoot, out);
    }
  }
}

/**
 * @param {HTMLElement} host
 * @returns {HTMLElement | null}
 */
function findAccountSheet(host) {
  const root = host.shadowRoot;
  if (!root) {
    return null;
  }

  const candidates = /** @type {HTMLElement[]} */ ([]);
  collectSheetCandidates(root, candidates);

  for (const el of candidates) {
    if (el instanceof HTMLDialogElement && el.open) {
      return el;
    }
  }

  for (const el of candidates) {
    try {
      if (el.matches(':popover-open')) {
        return el;
      }
    } catch {
      /* :popover-open unsupported */
    }
  }

  for (const el of candidates) {
    if (el.getAttribute('aria-hidden') === 'true') {
      continue;
    }
    if (el instanceof HTMLDialogElement) {
      return el;
    }
    if (el.hasAttribute('popover')) {
      return el;
    }
  }

  return candidates[0] ?? null;
}

/**
 * @param {HTMLElement} host
 * @returns {boolean}
 */
function isAccountSheetVisible(host) {
  const root = host.shadowRoot;
  if (!root) {
    return false;
  }

  const candidates = /** @type {HTMLElement[]} */ ([]);
  collectSheetCandidates(root, candidates);

  for (const el of candidates) {
    if (el instanceof HTMLDialogElement && el.open) {
      return true;
    }
  }

  for (const el of candidates) {
    try {
      if (el.matches(':popover-open')) {
        return true;
      }
    } catch {
      /* :popover-open unsupported */
    }
  }

  return false;
}

/**
 * @returns {number}
 */
function readHeaderPaddingPx() {
  const el = document.querySelector('.header-wrapper');
  if (!el) {
    return 8;
  }

  const raw = getComputedStyle(el).getPropertyValue('--header-padding').trim();
  if (!raw) {
    return 8;
  }

  if (raw.endsWith('rem')) {
    const rem = parseFloat(raw);
    const fs = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    return Number.isFinite(rem) ? rem * fs : 8;
  }

  if (raw.endsWith('px')) {
    const px = parseFloat(raw);
    return Number.isFinite(px) ? px : 8;
  }

  return 8;
}

/**
 * @returns {boolean}
 */
function isRtlDocument() {
  return (
    document.documentElement.dir === 'rtl' ||
    document.documentElement.getAttribute('dir') === 'rtl'
  );
}

/**
 * @param {HTMLElement} trigger
 * @param {HTMLElement} sheet
 */
function applySheetPosition(trigger, sheet) {
  const rect = trigger.getBoundingClientRect();
  const pad = readHeaderPaddingPx();
  const topPx = rect.bottom + pad;
  const rtl = isRtlDocument();

  sheet.style.setProperty('position', 'fixed');
  sheet.style.setProperty('top', `${Math.max(0, topPx)}px`);
  sheet.style.setProperty('bottom', 'auto');
  sheet.style.setProperty('margin', '0');

  if (rtl) {
    sheet.style.setProperty('left', `${Math.max(0, rect.left)}px`);
    sheet.style.setProperty('right', 'auto');
  } else {
    sheet.style.setProperty('right', `${Math.max(0, window.innerWidth - rect.right)}px`);
    sheet.style.setProperty('left', 'auto');
  }

  sheet.dataset.themeAccountSheetPositioned = 'true';
}

/**
 * @param {HTMLElement} sheet
 */
function clearSheetPosition(sheet) {
  if (!sheet.dataset.themeAccountSheetPositioned) {
    return;
  }

  sheet.style.removeProperty('position');
  sheet.style.removeProperty('top');
  sheet.style.removeProperty('right');
  sheet.style.removeProperty('left');
  sheet.style.removeProperty('bottom');
  sheet.style.removeProperty('margin');
  delete sheet.dataset.themeAccountSheetPositioned;
}

/**
 * @param {HTMLElement} host
 */
function bindShopifyAccountSheetAnchor(host) {
  if (host.dataset.accountSheetAnchorBound === 'true') {
    return;
  }

  host.dataset.accountSheetAnchorBound = 'true';

  const accountButton = host.closest('.account-button');
  const trigger =
    accountButton instanceof HTMLElement ? accountButton : host;

  let accountStickySuspended = false;

  /**
   * Scroll position when the account sheet opened (restore on close; avoids the browser
   * leaving the page scrolled after focus/keyboard/sheet layout).
   * @type {number | null}
   */
  let scrollTopWhenAccountOpened = null;

  const syncDocumentScrollTop = () =>
    document.scrollingElement?.scrollTop ?? 0;

  const suspendStickyForAccount = () => {
    if (accountStickySuspended) {
      return;
    }
    scrollTopWhenAccountOpened = syncDocumentScrollTop();
    accountStickySuspended = true;
    document.dispatchEvent(
      new CustomEvent(ThemeEvents.headerStickyScrollSuspend, {
        detail: {
          delta: 1,
          syncDocumentScrollTop: scrollTopWhenAccountOpened,
        },
      }),
    );
  };

  const resumeStickyForAccount = () => {
    if (!accountStickySuspended) {
      return;
    }
    accountStickySuspended = false;

    const restoreTop = scrollTopWhenAccountOpened;
    scrollTopWhenAccountOpened = null;

    if (restoreTop !== null) {
      window.scrollTo({ top: restoreTop, behavior: 'instant' });
    }

    document.dispatchEvent(
      new CustomEvent(ThemeEvents.headerStickyScrollSuspend, {
        detail: {
          delta: -1,
          syncDocumentScrollTop: syncDocumentScrollTop(),
        },
      }),
    );
  };

  const resumeIfSheetClosed = () => {
    if (!accountStickySuspended) {
      return;
    }
    if (!isAccountSheetVisible(host)) {
      resumeStickyForAccount();
    }
  };

  const reposition = () => {
    const sheet = findAccountSheet(host);
    if (!sheet) {
      return;
    }

    const visible =
      (sheet instanceof HTMLDialogElement && sheet.open) ||
      (() => {
        try {
          return sheet.matches(':popover-open');
        } catch {
          return false;
        }
      })();

    if (!visible) {
      clearSheetPosition(sheet);
      return;
    }

    if (!isDesktopAccountSheetLayout()) {
      clearSheetPosition(sheet);
      return;
    }

    applySheetPosition(/** @type {HTMLElement} */ (trigger), sheet);
  };

  const debouncedReposition = debounce(reposition, 50);

  host.addEventListener('open', () => {
    suspendStickyForAccount();
    requestAnimationFrame(() => {
      reposition();
      requestAnimationFrame(reposition);
    });
  });

  host.addEventListener('close', resumeStickyForAccount);

  const root = host.shadowRoot;
  if (root) {
    const shadowObserver = new MutationObserver(() => {
      resumeIfSheetClosed();
    });
    shadowObserver.observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open'],
    });
  }

  window.addEventListener('resize', debouncedReposition);
}

/**
 * @param {HTMLElement} host
 */
function watchShadowAndBind(host) {
  if (host.shadowRoot) {
    bindShopifyAccountSheetAnchor(host);
    return;
  }

  const observer = new MutationObserver(() => {
    if (host.shadowRoot) {
      observer.disconnect();
      bindShopifyAccountSheetAnchor(host);
    }
  });

  observer.observe(host, { childList: true });
  window.setTimeout(() => observer.disconnect(), 15000);
}

/**
 * @returns {Promise<void>}
 */
async function initShopifyAccountSheetAnchors() {
  await customElements.whenDefined('shopify-account');

  for (const host of document.querySelectorAll('header-actions shopify-account')) {
    if (host instanceof HTMLElement) {
      watchShadowAndBind(host);
    }
  }
}

void initShopifyAccountSheetAnchors();

document.addEventListener('shopify:section:load', () => {
  void initShopifyAccountSheetAnchors();
});
